import {
  BarChart3,
  ChartBarBig,
  ChartLine,
  ChartNetwork,
  Folder,
  Folders,
  Notebook,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CsvDownloadButton } from '~/components/common/csv-download-button';
import { Card, CardContent } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';

import { downloadAnalyticsDataDownloadCsv } from '../../services/analytics.service';
import type { DataDownloadCard, DataDownloadIcon } from '../../types/experiment.types';

interface Props {
  experimentId: string;
  cards: DataDownloadCard[];
  isLoading?: boolean | undefined;
  /** When true the body is replaced with an error card; header stays visible. */
  isError?: boolean | undefined;
  /** Encoded `?segment=` product filter (see `encodeSegmentFilter`); `undefined` = unscoped. */
  segment?: string | undefined;
}

const ICON_MAP: Record<DataDownloadIcon, typeof Folder> = {
  folder: Folder,
  chart: BarChart3,
};

/** Report-specific icon overrides keyed by the tile's `id`, taking precedence
 *  over the backend's coarser `folder`/`chart` `icon` field. Ids without an
 *  entry fall back to `ICON_MAP`. */
const REPORT_ICON_MAP: Partial<Record<string, typeof Folder>> = {
  'l2-category-did': ChartBarBig,
  'l3-category-did': Notebook,
  'weekly-trend-l2': ChartLine,
  'weekly-trend-l3': ChartNetwork,
  'revenue-lift-buckets': Folders,
};

/**
 * Grid of CSV-download cards rendered at the bottom of the category-mode
 * Performance tab. Each card's `id` (from `/data-downloads`) maps 1:1 to its
 * own streamed CSV report at `/data-downloads/{id}.csv`.
 */
export function ExperimentDetailedDataDownloads({
  experimentId,
  cards,
  isLoading,
  isError,
  segment,
}: Props) {
  const { t } = useTranslation('experiments');

  const handleDownload = async (card: DataDownloadCard): Promise<void> => {
    try {
      await downloadAnalyticsDataDownloadCsv(experimentId, card.id, segment);
    } catch {
      toast.error(
        t('detail.detailedDataDownloads.downloadError', 'Couldn’t download the CSV. Try again.'),
      );
    }
  };

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="min-w-0 space-y-4 p-6">
        {/* Header */}
        <div className="space-y-1">
          <h3 className="text-[16px] font-medium">
            {t('detail.detailedDataDownloads.title', 'Detailed Data Downloads')}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t(
              'detail.detailedDataDownloads.subtitle',
              'Full granular breakdowns for your analytics team',
            )}
          </p>
        </div>

        {isError ? (
          <div className="border-error/30 bg-error-50 space-y-1 rounded-md border p-4">
            <p className="text-foreground text-sm font-medium">
              {t('detail.detailedDataDownloads.errorTitle', 'Couldn’t load download bundles.')}
            </p>
            <p className="text-muted-foreground text-xs leading-5">
              {t('detail.detailedDataDownloads.errorBody', 'Refresh the page to try again.')}
            </p>
          </div>
        ) : isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-44 w-full rounded-lg" />
            ))}
          </div>
        ) : cards.length === 0 ? (
          <div className="bg-muted/30 rounded-md border border-dashed p-8 text-center">
            <p className="text-muted-foreground text-sm">
              {t('detail.detailedDataDownloads.empty', 'No download bundles available yet.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {cards.map((card) => (
              <DataDownloadTile key={card.id} card={card} onDownload={() => handleDownload(card)} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface DataDownloadTileProps {
  card: DataDownloadCard;
  onDownload: () => Promise<void>;
}

function DataDownloadTile({ card, onDownload }: DataDownloadTileProps) {
  const Icon = REPORT_ICON_MAP[card.id] ?? ICON_MAP[card.icon];
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = () => {
    if (isDownloading) return;
    setIsDownloading(true);
    void onDownload().finally(() => {
      setIsDownloading(false);
    });
  };

  return (
    <div className="flex h-full flex-col gap-3 rounded-xl border p-4">
      <div className="bg-primary/10 flex h-9 w-9 items-center justify-center rounded-md">
        <Icon className="text-primary h-5 w-5" aria-hidden="true" />
      </div>
      <div className="space-y-1">
        <p className="text-foreground text-sm font-semibold">{card.title}</p>
        <p className="text-muted-foreground text-xs leading-5">{card.description}</p>
      </div>
      <p className="text-muted-foreground text-xs">{card.summary}</p>
      {/* Anchors the button to the card's bottom edge regardless of how much
          description/summary text precedes it, so buttons stay aligned across
          a row of cards with uneven content length (grid stretches row height). */}
      <CsvDownloadButton isDownloading={isDownloading} onClick={handleClick} className="mt-auto" />
    </div>
  );
}
